import { EventEmitter, Injectable, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../core/services/api/auth.service';
import { LocationContextService } from '../layout/location-context/location-context.service';
import { AngularReactUrlMapperService } from './angular-react-url-mapper.service';

@Injectable({
  providedIn: 'root',
})
export class IframeNavigatorService {
  private iframe: HTMLIFrameElement | null = null;
  @Output() signOut: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private router: Router,
    private locationContextService: LocationContextService,
    private urlMapper: AngularReactUrlMapperService,
    private authService: AuthService
  ) {
    this.handleIframeEvents();
    this.handleRouterEvents();
    this.handleWarehouseFilterChanges();
  }

  setIframe(iframe: HTMLIFrameElement) {
    this.iframe = iframe;
    
    // Send the current warehouse filter to the newly loaded iframe
    if (this.iframe) {
      // Wait a bit for the iframe to be ready to receive messages
      setTimeout(() => {
        this.sendCurrentWarehouseFilter();
      }, 100);
    }
  }

  private sendCurrentWarehouseFilter() {
    if (this.iframe?.contentWindow) {
      // Get current location from the service's observable
      this.locationContextService.locationId$.pipe(
        take(1) // Take only the first emission (current value)
      ).subscribe((currentLocationId) => {
        if (this.iframe?.contentWindow) {
          this.iframe.contentWindow.postMessage(
            {
              type: "WAREHOUSE_FILTER",
              warehouseId: currentLocationId
            },
            {
              targetOrigin: environment.dashboard_url
            }
          );
        }
      });
    }
  }

  handleIframeEvents = () => {
    window.onmessage = (event: MessageEvent<{type: string, href: string, search?: Record<string, string>}>) => {
      if(event.data.type === 'NAVIGATE') {
        const reactPath = this.getFinalPath(event.data.href);
        // Only apply URL mapping for list pages that need it
        const needsMapping = this.urlMapper.shouldUseReact(reactPath.split('?')[0]);
        const angularPath = needsMapping ? this.urlMapper.reactToAngular(reactPath) : reactPath;
        console.log('React path:', reactPath, 'Angular path:', angularPath, 'Current URL:', this.router.url);
        if(angularPath.includes('/login')) {
          this.authService.logout();
          console.log('signing out');
          return;
        }
        if(angularPath !== this.router.url) {
          this.router.navigate([angularPath], {relativeTo: this.router.routerState.root});
        }
      } else if(event.data.type === 'REQUEST_WAREHOUSE_FILTER') {
        // React app is requesting the current warehouse filter state
        this.sendCurrentWarehouseFilter();
      }
    };
  }

  handleRouterEvents = () => {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && this.iframe?.contentWindow) {
        if(event.url === this.iframe.src) return;
        // Only apply URL mapping for list pages that need it
        const currentUrl = this.router.url;
        const needsMapping = this.urlMapper.shouldUseReact(currentUrl.split('?')[0]);
        const reactUrl = needsMapping ? this.urlMapper.angularToReact(currentUrl) : currentUrl;
        this.iframe.contentWindow.postMessage(
          {
            type: "NAVIGATE",
            href: reactUrl
          },
          {
            targetOrigin: environment.dashboard_url
          }
        );
      }
    });
  }

  handleWarehouseFilterChanges = () => {
    this.locationContextService.locationId$.subscribe((locationId) => {
      if (this.iframe?.contentWindow) {
        this.iframe.contentWindow.postMessage(
          {
            type: "WAREHOUSE_FILTER",
            warehouseId: locationId
          },
          {
            targetOrigin: environment.dashboard_url
          }
        );
      }
    });
  }

  getFinalPath = (path: string) => {
    const reroutePath = REROUTED_REACT_LINKS.find(link => path.startsWith(link.from));
    path = path.replace(reroutePath?.from, reroutePath?.to);
    path = path.replace('/legacy', '');

    return path;
  }
}

const REROUTED_REACT_LINKS = [
  {
    from: `/warehouses`,
    to: '/locations/warehouse',
  },
]